import axios from 'axios';
import {
  getScheduleById,
  getSchedulesByBreakdownId,
  getSlotsByScheduleId,
  getCalDatesByScheduleId
} from '../../api/schedule';
import {
  CalDateDataCache,
  ScheduleDataCache,
  SlotDataCache
} from '../../types/cache.types';
import { ScheduleInfo } from '../../types/schedule.types';

type CacheAccessors = {
  getSlotCache: (scheduleId: string) => SlotDataCache | null;
  getCalDateCache: (scheduleId: string) => CalDateDataCache | null;
  getScheduleCache: (breakdownId: string) => ScheduleDataCache | null;
  setSlotCache: (scheduleId: string, data: Partial<SlotDataCache>) => void;
  setCalDateCache: (scheduleId: string, data: Partial<CalDateDataCache>) => void;
  setScheduleCache: (breakdownId: string, data: Partial<ScheduleDataCache>) => void;
};

export type EnsureExportScheduleDataParams = CacheAccessors & {
  scheduleId: string;
  breakdownId?: string;
  /** 制作日历、详细拍摄计划等需要 CalDate 缓存 */
  requireCalDates?: boolean;
};

const inflightByKey = new Map<string, Promise<void>>();

/** 导出选项是否需要拍摄计划 Slot 数据 */
export function exportOptionNeedsScheduleSlotData(optionId: string | null | undefined): boolean {
  if (!optionId) return false;
  return (
    optionId === 'schedule-detail-pdf' ||
    optionId === 'one-line-schedule-pdf' ||
    optionId === 'schedule-calendar-pdf' ||
    optionId === 'element-shooting-dates-summary-excel' ||
    optionId === 'element-shooting-dates-detail-excel' ||
    optionId === 'element-qty-excel'
  );
}

export function exportOptionNeedsScheduleCalDateData(optionId: string | null | undefined): boolean {
  if (!optionId) return false;
  return (
    optionId === 'schedule-detail-pdf' ||
    optionId === 'one-line-schedule-pdf' ||
    optionId === 'schedule-calendar-pdf'
  );
}

async function ensureBreakdownScheduleList(
  breakdownId: string,
  getScheduleCache: CacheAccessors['getScheduleCache'],
  setScheduleCache: CacheAccessors['setScheduleCache']
): Promise<void> {
  const existing = getScheduleCache(breakdownId);
  if ((existing?.schedules?.length ?? 0) > 0) return;

  const listRes = await getSchedulesByBreakdownId(breakdownId);
  const schedules = listRes.data || [];
  setScheduleCache(breakdownId, {
    ...existing,
    schedules
  });
}

function mergeScheduleIntoBreakdownCache(
  breakdownId: string,
  scheduleId: string,
  loadedSchedule: ScheduleInfo,
  loadedSlots: SlotDataCache['slots'],
  loadedCalDates: CalDateDataCache['calDates'],
  getScheduleCache: CacheAccessors['getScheduleCache'],
  setScheduleCache: CacheAccessors['setScheduleCache']
) {
  const scheduleSlice = getScheduleCache(breakdownId);
  let mergedSchedules: ScheduleInfo[];
  if (scheduleSlice?.schedules?.length) {
    mergedSchedules = scheduleSlice.schedules.map((p) =>
      p.scheduleID === scheduleId ? { ...p, ...loadedSchedule } : p
    );
    if (!mergedSchedules.some((p) => p.scheduleID === scheduleId)) {
      mergedSchedules = [...mergedSchedules, loadedSchedule];
    }
  } else {
    mergedSchedules = [loadedSchedule];
  }

  setScheduleCache(breakdownId, {
    ...scheduleSlice,
    schedules: mergedSchedules,
    currentScheduleId: scheduleSlice?.currentScheduleId ?? scheduleId,
    currentScheduleInfo:
      scheduleSlice?.currentScheduleId === scheduleId
        ? loadedSchedule
        : scheduleSlice?.currentScheduleInfo ?? null,
    currentSlots:
      scheduleSlice?.currentScheduleId === scheduleId
        ? loadedSlots
        : scheduleSlice?.currentSlots ?? [],
    currentCalDates:
      scheduleSlice?.currentScheduleId === scheduleId
        ? loadedCalDates
        : scheduleSlice?.currentCalDates ?? []
  });
}

/** 导出对话框打开时：确保分场下的拍摄计划列表已在缓存中 */
export async function ensureBreakdownSchedulesListLoaded(
  breakdownId: string,
  getScheduleCache: CacheAccessors['getScheduleCache'],
  setScheduleCache: CacheAccessors['setScheduleCache']
): Promise<void> {
  await ensureBreakdownScheduleList(breakdownId, getScheduleCache, setScheduleCache);
}

/**
 * 导出前确保所选拍摄计划的 Slot（及可选 CalDate）已写入缓存；缺失时从服务器拉取。
 */
export async function ensureExportScheduleDataLoaded(
  params: EnsureExportScheduleDataParams
): Promise<void> {
  const {
    scheduleId,
    breakdownId,
    requireCalDates = false,
    getSlotCache,
    getCalDateCache,
    getScheduleCache,
    setSlotCache,
    setCalDateCache,
    setScheduleCache
  } = params;

  const inflightKey = `${scheduleId}:${requireCalDates ? 'cal' : 'slot'}`;
  const existing = inflightByKey.get(inflightKey);
  if (existing) {
    await existing;
    return;
  }

  const task = (async () => {
    if (breakdownId) {
      await ensureBreakdownScheduleList(breakdownId, getScheduleCache, setScheduleCache);
    }

    const slotCache = getSlotCache(scheduleId);
    const hasSlotData = !!slotCache?.scheduleInfo;
    const hasCalData = getCalDateCache(scheduleId) != null;

    if (hasSlotData && (!requireCalDates || hasCalData)) {
      return;
    }

    let loadedSchedule = slotCache?.scheduleInfo ?? null;
    let loadedSlots = slotCache?.slots ?? [];
    let loadedCalDates = getCalDateCache(scheduleId)?.calDates ?? [];

    if (!hasSlotData) {
      const [scheduleRes, slotsRes] = await Promise.all([
        getScheduleById(scheduleId),
        getSlotsByScheduleId(scheduleId)
      ]);
      loadedSchedule = scheduleRes.data;
      loadedSlots = slotsRes.data || [];
      setSlotCache(scheduleId, {
        scheduleInfo: loadedSchedule,
        slots: loadedSlots
      });
    }

    if (requireCalDates && !hasCalData) {
      const calRes = await getCalDatesByScheduleId(scheduleId);
      loadedCalDates = calRes.data || [];
      setCalDateCache(scheduleId, { calDates: loadedCalDates });
    }

    const resolvedBreakdownId =
      breakdownId || loadedSchedule?.scheduleBelongBreakdownID || undefined;
    if (resolvedBreakdownId && loadedSchedule) {
      mergeScheduleIntoBreakdownCache(
        resolvedBreakdownId,
        scheduleId,
        loadedSchedule,
        loadedSlots,
        loadedCalDates,
        getScheduleCache,
        setScheduleCache
      );
    }
  })();

  inflightByKey.set(inflightKey, task);
  try {
    await task;
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      throw new Error('EXPORT_SCHEDULE_NOT_FOUND');
    }
    throw err;
  } finally {
    inflightByKey.delete(inflightKey);
  }
}
